/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {FormControl} from '@angular/forms';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {DISMISS_MSG} from '../../../common/constants';
import {AuthenticationService} from '../../../core/authentication/authentication.service';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {ColumnHeader} from '../../../core/models/column-header.model';
import {WorksheetExportRequest} from '../../../core/models/worksheet-export-request.model';
import {AdminBrandfamDialogGetRequest} from '../../../models/admin-brandfam-dialog-get-request.model';
import {GetBrandFamResultList} from '../../../models/admin-brandfam-result-list.model';
import {AdminBrandfamTableGetRequest} from '../../../models/admin-brandfam-table-get-request.model';
import {AdminBrandfamUpdateRequest} from '../../../models/admin-brandfam-update-request.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {ExcelExportService} from '../../../services/excel-export.service';
import {ManageBrandFamilyService} from '../../../services/manage-brand-family-service';

export const REGION_PATH = 'regions';
export const SUBREGION_PATH = 'subregions';
export const PLANNING_MARKET_PATH = 'planning-market';
export const MARKET_DIVISION_PATH = 'market-division';
export const BRAND_PATH = 'brand';
export const LOCAL_BRAND_PATH = 'local-brand';
export const PROGRAM_CODE_PATH = 'program-code';
export const STYLE_CODE_PATH = 'style-code';
export const BODY_STYLE_PATH = 'body-style';
export const BRANDFAM_PATH = 'brandfam';
export const GLOBAL_SEGMENT_PATH = 'global-segment';
export const PROP_SEGMENT_PATH = 'propulsion-segment';

@Component({
    selector: 'app-admin-manage-brand-family',
    templateUrl: './manage-brand-family.component.html',
    styleUrls: ['./manage-brand-family.component.scss'],
    standalone: false
})
export class ManageBrandFamilyComponent implements OnInit {

  constructor(public snackBar: FabMessageService,
              private readonly brandfamService: ManageBrandFamilyService,
              private readonly authService: AuthenticationService,
              public readonly excelService: ExcelExportService) {
  }

  inputControl: FormControl = new FormControl();


  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  user: string;

  pageSize: number;
  page: number;
  tableLength: number;

  showPlanningMarket: boolean;

  columnDefs: ColDef[] = [
    {headerName: 'Port', field: 'PORT'},
    {headerName: 'Subregion', field: 'SUBREGION', filter: AutocompleteFilterComponent},
    {headerName: 'Planning Market', field: 'PLNGMKT', filter: AutocompleteFilterComponent},
    {headerName: 'Market Division', field: 'MKTDIV', filter: AutocompleteFilterComponent},
    {headerName: 'Brand', field: 'BRAND', filter: AutocompleteFilterComponent},
    {headerName: 'Local Brand', field: 'LBRAND', filter: AutocompleteFilterComponent},
    {headerName: 'Program Code', field: 'PRGMCODE', filter: AutocompleteFilterComponent},
    {headerName: 'Style Code', field: 'STYLECODE', filter: AutocompleteFilterComponent},
    {headerName: 'Body Style', field: 'BODYSTYLE', filter: AutocompleteFilterComponent},
    {headerName: 'Brand Family', field: 'BRANDFAM', filter: AutocompleteFilterComponent},
    {headerName: 'Global Segment', field: 'GSEG', filter: AutocompleteFilterComponent},
    {headerName: 'Propulsion Segment', field: 'PROP_SEG', filter: AutocompleteFilterComponent},
    {headerName: 'SOS Gap', field: 'SOS_GAP'},
    {headerName: 'EOS Gap', field: 'EOS_GAP'}
  ];


  displayedColumns: string[];
  tableColumns: ColumnHeader[] = [
    {name: 'PORT', label: 'Port'},
    {name: 'SUBREGION', label: 'Subregion'},
    {name: 'PLNGMKT', label: 'Planning Market'},
    {name: 'MKTDIV', label: 'Market Division'},
    {name: 'BRAND', label: 'Brand'},
    {name: 'LBRAND', label: 'Local Brand'},
    {name: 'PRGMCODE', label: 'Program Code'},
    {name: 'STYLECODE', label: 'Style Code'},
    {name: 'BODYSTYLE', label: 'Body Style'},
    {name: 'BRANDFAM', label: 'Brand Family'},
    {name: 'GSEG', label: 'Global Segment'},
    {name: 'PROP_SEG', label: 'Propulsion Segment'},
    {name: 'SOS_GAP', label: 'SOS Gap'},
    {name: 'EOS_GAP', label: 'EOS Gap'}];
  tableColumnsNoPlngMkt: ColumnHeader[] = [
    {name: 'PORT', label: 'Port'},
    {name: 'SUBREGION', label: 'Subregion'},
    {name: 'MKTDIV', label: 'Market Division'},
    {name: 'BRAND', label: 'Brand'},
    {name: 'LBRAND', label: 'Local Brand'},
    {name: 'PRGMCODE', label: 'Program Code'},
    {name: 'STYLECODE', label: 'Style Code'},
    {name: 'BODYSTYLE', label: 'Body Style'},
    {name: 'BRANDFAM', label: 'Brand Family'},
    {name: 'GSEG', label: 'Global Segment'},
    {name: 'PROP_SEG', label: 'Propulsion Segment'},
    {name: 'SOS_GAP', label: 'SOS Gap'},
    {name: 'EOS_GAP', label: 'EOS Gap'}];
  resultData: GetBrandFamResultList[] = [];
  worksheetExportRequest: WorksheetExportRequest;

  selectedEditCategory: number;
  editOptions: string[] = [];
  selectedEditOption: string;

  regionsList: string[] = [];
  selectedRegion: string;
  queryRegion: string;
  subregions: SelectableOptions = new SelectableOptions();
  planningMarkets: SelectableOptions = new SelectableOptions();
  marketDivisions: SelectableOptions = new SelectableOptions();
  brands: SelectableOptions = new SelectableOptions();
  localBrands: SelectableOptions = new SelectableOptions();
  programCodes: SelectableOptions = new SelectableOptions();
  styleCodes: SelectableOptions = new SelectableOptions();
  bodyStyles: SelectableOptions = new SelectableOptions();
  brandFams: SelectableOptions = new SelectableOptions();
  globalSegments: SelectableOptions = new SelectableOptions();
  propSegments: SelectableOptions = new SelectableOptions();
  filteredOptions: Observable<string[]>;
  showSpinner: boolean;
  isGsegBtnDisabled: boolean;
  isPropBtnDisabled: boolean;

  ngOnInit(): void {
    this.setVariables();
    this.initializeRegion();
  }

  /**
   *  sets initial values for variables on init
   */
  setVariables(): void {
    this.user = this.fetchUser();
    this.page = 0;
    this.pageSize = 20;
    this.showPlanningMarket = true;
    this.tableLength = 0;
    this.selectedEditCategory = -1;
    this.selectedEditOption = '';
    this.showSpinner = false;
  }

  /**
   * Uses the auth service to get the GMID of the user for the queries
   * @return string the GMID of the user
   */
  fetchUser(): string {
    return this.authService.userInfo.gmid;
  }

  /**
   *  Creates a request for the regions and assigns the response to the regions list
   */
  initializeRegion(): void {
    const request: AdminBrandfamDialogGetRequest =
      {
        path: REGION_PATH,
        user: this.user,
        selectedRegion: '',
        selectedSubregion: '',
        selectedPlanningMarket: '',
        selectedMarketDivision: '',
        selectedBrand: '',
        selectedLocalBrand: '',
        selectedProgramCode: '',
        selectedStyleCode: '',
        selectedBodyStyle: '',
        selectedBrandFam: '',
        selectedGSeg: '',
        selectedPropSeg: ''
      };

    this.brandfamService.getData(request).subscribe(
      (data) => {
        this.regionsList = data;
      },
      err => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   *  the method that is called when anything is changed in the UI
   *  @param attribute the attribute that changed
   */
  onChange(attribute: string): void {
    this.requestDataAndUpdateLists(attribute);
  }

  /**
   *  gets a request and sets the appropriate list
   *  @param attribute the attribute that changed
   */
  requestDataAndUpdateLists(attribute: string): void {
    const request = this.buildRequestAndCascadeUpdate(attribute);
    this.brandfamService.getData(request).subscribe(
      (data) => {
        this.setDropdownOptions(request.path, data);
      },
      err => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   *  builds the request object and clears the lists higher than the altered list
   *  @param attribute the attribute that changed
   *  @return GetAdminBrandFamDialogRequest the request object to use to call the service
   */
  buildRequestAndCascadeUpdate(attribute: string): AdminBrandfamDialogGetRequest {
    const request: AdminBrandfamDialogGetRequest =
      {
        path: '',
        user: this.user,
        selectedRegion: this.selectedRegion,
        selectedSubregion: this.convertForQuery(this.subregions.selectedOptions, this.subregions.options),
        selectedPlanningMarket: this.convertForQuery(this.planningMarkets.selectedOptions, this.planningMarkets.options),
        selectedMarketDivision: this.convertForQuery(this.marketDivisions.selectedOptions, this.marketDivisions.options),
        selectedBrand: this.convertForQuery(this.brands.selectedOptions, this.brands.options),
        selectedLocalBrand: this.convertForQuery(this.localBrands.selectedOptions, this.localBrands.options),
        selectedProgramCode: this.convertForQuery(this.programCodes.selectedOptions, this.programCodes.options),
        selectedStyleCode: this.convertForQuery(this.styleCodes.selectedOptions, this.styleCodes.options),
        selectedBodyStyle: this.convertForQuery(this.bodyStyles.selectedOptions, this.bodyStyles.options),
        selectedBrandFam: this.convertForQuery(this.brandFams.selectedOptions, this.brandFams.options),
        selectedGSeg: this.convertForQuery(this.globalSegments.selectedOptions, this.globalSegments.options),
        selectedPropSeg: this.convertForQuery(this.propSegments.selectedOptions, this.propSegments.options)
      };

    switch (attribute.toUpperCase()) {
      case 'REGION':
        request.path = SUBREGION_PATH;
        this.cascadeUpdateDropdowns(1);
        break;
      case 'SUBREGION':
        request.path = PLANNING_MARKET_PATH;
        this.cascadeUpdateDropdowns(2);
        break;
      case 'PLNGMKT':
        request.path = MARKET_DIVISION_PATH;
        this.cascadeUpdateDropdowns(3);
        break;
      case 'MKTDIV':
        request.path = BRAND_PATH;
        this.cascadeUpdateDropdowns(4);
        break;
      case 'BRAND':
        request.path = LOCAL_BRAND_PATH;
        this.cascadeUpdateDropdowns(5);
        break;
      case 'LBRAND':
        request.path = PROGRAM_CODE_PATH;
        this.cascadeUpdateDropdowns(6);
        break;
      case 'PRGMCODE':
        request.path = STYLE_CODE_PATH;
        this.cascadeUpdateDropdowns(7);
        break;
      case 'STYLECODE':
        request.path = BODY_STYLE_PATH;
        this.cascadeUpdateDropdowns(8);
        break;
      case 'BODYSTYLE':
        request.path = BRANDFAM_PATH;
        this.cascadeUpdateDropdowns(9);
        break;
      case 'BRANDFAM':
        request.path = GLOBAL_SEGMENT_PATH;
        this.cascadeUpdateDropdowns(10);
        break;
      case 'GSEG':
        request.path = PROP_SEGMENT_PATH;
        this.cascadeUpdateDropdowns(11);
        break;
    }
    return request;
  }

  /**
   *  Maps an input to a list of selectable items
   *  @param startIndex the index in the list to start resetting the options. Uses case fall through
   */
  private cascadeUpdateDropdowns(startIndex: number): void {
    switch (startIndex) {
      case 1:
        this.subregions.options = [];
        this.subregions.selectedOptions = [];
      // eslint-disable-next-line
      case 2:
        this.planningMarkets.options = [];
        this.planningMarkets.selectedOptions = [];
      // eslint-disable-next-line
      case 3:
        this.marketDivisions.options = [];
        this.marketDivisions.selectedOptions = [];
      // eslint-disable-next-line
      case 4:
        this.brands.options = [];
        this.brands.selectedOptions = [];
      // eslint-disable-next-line
      case 5:
        this.localBrands.options = [];
        this.localBrands.selectedOptions = [];
      // eslint-disable-next-line
      case 6:
        this.programCodes.options = [];
        this.programCodes.selectedOptions = [];
      // eslint-disable-next-line no-fallthrough
      case 7:
        this.styleCodes.options = [];
        this.styleCodes.selectedOptions = [];
      // eslint-disable-next-line
      case 8:
        this.bodyStyles.options = [];
        this.bodyStyles.selectedOptions = [];
      // eslint-disable-next-line
      case 9:
        this.brandFams.options = [];
        this.brandFams.selectedOptions = [];
      // eslint-disable-next-line
      case 10:
        this.globalSegments.options = [];
        this.globalSegments.selectedOptions = [];
      // eslint-disable-next-line
      case 11:
        this.propSegments.options = [];
        this.propSegments.selectedOptions = [];
    }
  }

  /**
   * gets a request and sets the appropriate options
   * @param path request path
   * @param data array of appropriate options
   */
  setDropdownOptions(path: string, data: string[]): void {
    switch (path) {
      case SUBREGION_PATH:
        this.subregions.options = data;
        break;
      case PLANNING_MARKET_PATH:
        this.planningMarkets.options = data;
        break;
      case MARKET_DIVISION_PATH:
        this.marketDivisions.options = data;
        break;
      case BRAND_PATH:
        this.brands.options = data;
        break;
      case LOCAL_BRAND_PATH:
        this.localBrands.options = data;
        break;
      case PROGRAM_CODE_PATH:
        this.programCodes.options = data;
        break;
      case STYLE_CODE_PATH:
        this.styleCodes.options = data;
        break;
      case BODY_STYLE_PATH:
        this.bodyStyles.options = data;
        break;
      case BRANDFAM_PATH:
        this.brandFams.options = data;
        break;
      case GLOBAL_SEGMENT_PATH:
        this.globalSegments.options = data;
        break;
      case PROP_SEGMENT_PATH:
        this.propSegments.options = data;
        break;
    }
  }

  /**
   * fills every dropdown past planning market with 'All' option
   */
  selectEverything(): void {
    this.showSpinner = true;
    this.marketDivisions.selectedOptions = this.marketDivisions.options;
    let request = this.buildRequestAndCascadeUpdate('MKTDIV');
    this.brandfamService.getData(request).subscribe(
      (data) => {
        this.setDropdownOptions(request.path, data);
      },
      err => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.brands.selectedOptions = this.brands.options;
        request = this.buildRequestAndCascadeUpdate('BRAND');
        this.brandfamService.getData(request).subscribe(
          (data) => {
            this.setDropdownOptions(request.path, data);
          },
          err => {
            this.snackBar.displayError(err, DISMISS_MSG);
            this.showSpinner = false;
          }, () => {
            this.localBrands.selectedOptions = this.localBrands.options;
            request = this.buildRequestAndCascadeUpdate('LBRAND');
            this.brandfamService.getData(request).subscribe(
              (data) => {
                this.setDropdownOptions(request.path, data);
              },
              err => {
                this.snackBar.displayError(err, DISMISS_MSG);
                this.showSpinner = false;
              }, () => {
                this.programCodes.selectedOptions = this.programCodes.options;
                request = this.buildRequestAndCascadeUpdate('PRGMCODE');
                this.brandfamService.getData(request).subscribe(
                  (data) => {
                    this.setDropdownOptions(request.path, data);
                  },
                  err => {
                    this.snackBar.displayError(err, DISMISS_MSG);
                    this.showSpinner = false;
                  }, () => {
                    this.styleCodes.selectedOptions = this.styleCodes.options;
                    request = this.buildRequestAndCascadeUpdate('STYLECODE');
                    this.brandfamService.getData(request).subscribe(
                      (data) => {
                        this.setDropdownOptions(request.path, data);
                      },
                      err => {
                        this.snackBar.displayError(err, DISMISS_MSG);
                        this.showSpinner = false;
                      }, () => {
                        this.bodyStyles.selectedOptions = this.bodyStyles.options;
                        request = this.buildRequestAndCascadeUpdate('BODYSTYLE');
                        this.brandfamService.getData(request).subscribe(
                          (data) => {
                            this.setDropdownOptions(request.path, data);
                          },
                          err => {
                            this.snackBar.displayError(err, DISMISS_MSG);
                            this.showSpinner = false;
                          }, () => {
                            this.brandFams.selectedOptions = this.brandFams.options;
                            request = this.buildRequestAndCascadeUpdate('BRANDFAM');
                            this.brandfamService.getData(request).subscribe(
                              (data) => {
                                this.setDropdownOptions(request.path, data);
                              },
                              err => {
                                this.snackBar.displayError(err, DISMISS_MSG);
                                this.showSpinner = false;
                              }, () => {
                                this.globalSegments.selectedOptions = this.globalSegments.options;
                                request = this.buildRequestAndCascadeUpdate('GSEG');
                                this.brandfamService.getData(request).subscribe(
                                  (data) => {
                                    this.setDropdownOptions(request.path, data);
                                  },
                                  err => {
                                    this.snackBar.displayError(err, DISMISS_MSG);
                                    this.showSpinner = false;
                                  }, () => {
                                    this.propSegments.selectedOptions = this.propSegments.options;
                                    this.showSpinner = false;
                                  }
                                );
                              }
                            );
                          }
                        );
                      }
                    );
                  }
                );
              }
            );
          }
        );
      }
    );
  }

  /**
   * sets the query options to be independent of what is selected after query is run for edits
   */
  setQuery(): void {
    this.queryRegion = this.selectedRegion;
    this.subregions.query = this.convertForQuery(this.subregions.selectedOptions, this.subregions.options);
    this.planningMarkets.query = this.convertForQuery(this.planningMarkets.selectedOptions, this.planningMarkets.options);
    this.marketDivisions.query = this.convertForQuery(this.marketDivisions.selectedOptions, this.marketDivisions.options);
    this.brands.query = this.convertForQuery(this.brands.selectedOptions, this.brands.options);
    this.localBrands.query = this.convertForQuery(this.localBrands.selectedOptions, this.localBrands.options);
    this.programCodes.query = this.convertForQuery(this.programCodes.selectedOptions, this.programCodes.options);
    this.styleCodes.query = this.convertForQuery(this.styleCodes.selectedOptions, this.styleCodes.options);
    this.bodyStyles.query = this.convertForQuery(this.bodyStyles.selectedOptions, this.bodyStyles.options);
    this.brandFams.query = this.convertForQuery(this.brandFams.selectedOptions, this.brandFams.options);
    this.globalSegments.query = this.convertForQuery(this.globalSegments.selectedOptions, this.globalSegments.options);
    this.propSegments.query = this.convertForQuery(this.propSegments.selectedOptions, this.propSegments.options);
  }

  /**
   * differentiates between the button click and a new page event
   */
  filteredReportOnClick(): void {
    this.resultData = [];
    this.setQuery();
    this.getFilteredBrandfamResults();
  }

  /**
   *  gets the list of results filtered by the dropdowns
   */
  getFilteredBrandfamResults(): void {
    const request: AdminBrandfamTableGetRequest =
      {
        path: 'result',
        user: this.user,
        selectedRegion: this.queryRegion,
        selectedSubregion: this.subregions.query,
        selectedPlanningMarket: this.planningMarkets.query,
        selectedMarketDivision: this.marketDivisions.query,
        selectedBrand: this.brands.query,
        selectedLocalBrand: this.localBrands.query,
        selectedProgramCode: this.programCodes.query,
        selectedStyleCode: this.styleCodes.query,
        selectedBodyStyle: this.bodyStyles.query,
        selectedBrandFam: this.brandFams.query,
        selectedGSeg: this.globalSegments.query,
        selectedPropSeg: this.propSegments.query,
        showPlngmkt: this.showPlanningMarket ? 1 : 0,
        page: this.page,
        pageSize: this.pageSize
      };
    this.showSpinner = true;
    this.brandfamService.getTableData(request).subscribe(
      data => {
        this.resultData = data;
        this.tableLength = data.length;
      },
      err => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      () => {
        this.resetMainTable();
        this.setGsegBtnDisabled();
        this.setPropBtnDisabled();
        this.setExportRequest();
        this.showSpinner = false;
      }
    );
  }

  /**
   * Fills the update dropdown
   */
  getUpdateDropdownList(): void {
    let path: string;
    let param: string;
    this.selectedEditOption = '';
    switch (this.selectedEditCategory) {
      case 0:
        path = 'brandfams';
        param = this.queryRegion;
        break;
      case 1:
        path = 'global-segments';
        param = null;
        break;
      case 2:
        path = 'propulsion-segments';
        param = this.globalSegments.query;
        break;
    }
    this.brandfamService.getUpdateDropdownData(this.user, param, path).subscribe(
      (data) => {
        this.editOptions = data;
        if (this.selectedEditCategory === 0) {
          this.filteredOptions = this.inputControl.valueChanges
          .pipe(startWith(''), map(val => this._filter(val)));
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    );
  }

  /**
   * Updates the filtered list (brandfam, global segment, or propulsion segment) with the selected value
   */
  updateBrandfamGSegPropSeg(): void {

    const req: AdminBrandfamUpdateRequest =
      {
        path: '',
        user: this.user,
        selectedRegion: this.queryRegion,
        selectedSubregion: this.subregions.query,
        selectedPlanningMarket: this.planningMarkets.query,
        selectedMarketDivision: this.marketDivisions.query,
        selectedBrand: this.brands.query,
        selectedLocalBrand: this.localBrands.query,
        selectedProgramCode: this.programCodes.query,
        selectedStyleCode: this.styleCodes.query,
        selectedBodyStyle: this.bodyStyles.query,
        selectedBrandFam: this.brandFams.query,
        selectedGSeg: this.globalSegments.query,
        selectedPropSeg: this.propSegments.query,
        newValue: this.selectedEditOption
      };
    switch (this.selectedEditCategory) {
      case 0:
        req.path = 'brandfams';
        break;
      case 1:
        req.path = 'global-segments';
        break;
      case 2:
        req.path = 'propulsion-segments';
        break;
    }

    this.brandfamService.updateTheList(req).subscribe({
      error: err => {
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        switch (this.selectedEditCategory) {
          case 0:
            this.onChange('BODYSTYLE');
            break;
          case 1:
            this.onChange('BRANDFAM');
            break;
          case 2:
            this.onChange('GSEG');
            break;
        }
        this.editOptions = [];
        this.selectedEditCategory = -1;
        this.selectedEditOption = '';
        this.snackBar.displaySuccess('Successfully updated brandfam table', DISMISS_MSG);
      }
    });
  }

  /**
   * Helper function used to set the export request
   */
  setExportRequest(): void {
    const currDate = new Date().toISOString();
    const excelData = new Map<string, GetBrandFamResultList[]>();
    excelData['BrandFam'] = this.resultData;
    this.worksheetExportRequest = this.excelService.createExportRequestBody('BrandFams_' + currDate,
      excelData,
      [['BrandFam', this.showPlanningMarket ? this.tableColumns : this.tableColumnsNoPlngMkt]]);
  }

  /**
   * resets and clears the dropdowns
   */
  resetFilters(): void {
    this.selectedRegion = '';
    this.cascadeUpdateDropdowns(1);
  }

  /**
   * Resets the data referring to the edit options of the main table
   */
  resetMainTable(): void {
    this.editOptions = [];
    this.selectedEditCategory = -1;
    this.selectedEditOption = '';
  }

  /**
   * Util Functions
   */

  /**
   * check if list is empty - used to disable select all option
   * @return boolean true if empty, false otherwise
   */
  isListEmpty(list: any[]): boolean {
    return list.length === 0;
  }

  /**
   *  Maps an input to a list of selectable items
   *  @param selectedOptions list of selected items to turn into one string for the stored procedure
   *  @param allOptions list of all possible options for category
   *  @return string the string to be used as a parameter
   */
  convertForQuery(selectedOptions: string[], allOptions: string[]): string {
    let result = '';
    if (selectedOptions.length === allOptions.length) {
      return 'all';
    } else {
      for (const item of selectedOptions) {
        result = `${result}${item}, `;
      }
    }
    return result.substring(0, result.length - 2);
  }

  /**
   * Used when switching tables, sets the page to 0 and size to 10
   */
  resetPage(): void {
    this.page = 0;
    this.pageSize = 10;
  }


  /**
   * Click Functions
   */


  /**
   * filters the BrandFam autocomplete dropdrown with users input
   * @param val users input
   */
  _filter(val: string): string[] {
    return this.editOptions.filter(option =>
      option.toLowerCase().indexOf(val.toLowerCase()) === 0);
  }

  /**
   * Helper method to set the boolean as to whether the Global Segment can be modified
   * after getting the filtered report. If all entries in the filtered report are in
   * the Vehicle Portfolio, they cannot modify the Global Segment.
   */
  private setGsegBtnDisabled(): void {
    // If a single entry does not have a 'P' as the PORT value, enabled Global Segment button
    this.isGsegBtnDisabled = !this.resultData.some(e => e.PORT !== 'P');
  }

  /**
   * Helper method to set the boolean as to whether the Propulsion Segment can be modified
   * after getting the filtered report. If the Global Segment filtered values is All then
   * Propulsion Segment button to modify is disabled.
   */
  private setPropBtnDisabled(): void {
    this.isPropBtnDisabled = this.globalSegments.selectedOptions.includes('All') || this.globalSegments.selectedOptions.length > 1;
  }

  /**
   * Gets text for tooltip on dropdowns based on length of the selected options
   * @param selectedOptions Selected options in dropdown
   * @return Formatted string to use for tooltip
   */
  getTooltipText(selectedOptions: string[]): string {
    let returnString = '';
    if (selectedOptions.length === 1) {
      returnString = selectedOptions[0];
    }
    if (selectedOptions.length > 1) {
      returnString = selectedOptions[0] + ' (+' + (selectedOptions.length - 1);

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      selectedOptions.length === 2 ? returnString += ' other)' : returnString += ' others)';
    }
    return returnString;
  }

}
